import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/lib/utils';

const baseVariants = cva(
    'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl text-sm font-medium transition-all duration-200 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none  [&_svg]:shrink-0 outline-none',

    {
        variants: {
            variant: {
                default:
                    'bg-primary text-primary-foreground shadow-sm hover:bg-primary/90 hover:shadow-md active:scale-[0.97] ' +
                    'bg-gradient-to-br from-primary/90 to-primary',
                destructive:
                    'bg-destructive text-white shadow-sm hover:bg-destructive/90 hover:shadow-md active:scale-[0.97] dark:text-destructive-foreground',
                outline:
                    'border border-border/60 bg-background shadow-sm hover:bg-muted/50 hover:text-foreground active:scale-[0.97]',
                secondary:
                    'bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80 hover:shadow-md active:scale-[0.97] ' +
                    'bg-gradient-to-br from-secondary/90 to-secondary',
                ghost: 'hover:bg-muted/50 text-foreground active:scale-[0.97]',
                link: 'text-primary underline-offset-4 hover:underline active:opacity-80',
                // No built-in look: for controls styled entirely via className
                bare: '',
            },

            size: {
                default: 'h-10 px-5 rounded-xl has-[>svg]:px-4',
                sm: 'h-9 px-4 rounded-full has-[>svg]:px-3',
                lg: 'h-12 px-6 rounded-xl text-base has-[>svg]:px-5',
                icon: 'size-10 rounded-xl',
                bare: '',
            },
        },

        defaultVariants: {
            variant: 'default',
            size: 'default',
        },
    },
);

const buttonVariants = (props?: Parameters<typeof baseVariants>[0]): string =>
    props?.variant === 'bare' ? cn(props.className) : baseVariants(props);

const Button = React.forwardRef<
    HTMLButtonElement,
    React.ComponentProps<'button'> &
        VariantProps<typeof baseVariants> & {
            asChild?: boolean;
        }
>(({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';

    return (
        <Comp
            className={cn(buttonVariants({ variant, size, className }))}
            ref={ref}
            {...props}
        />
    );
});
Button.displayName = 'Button';

export { Button, buttonVariants };
